import type { RouteObject } from 'react-router';

import type { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { listingApiFetchInternship } from 'modules/listing/api/internship/listingApiFetchInternship';
import { isListingExpired } from 'modules/listing/helpers/isListingExpired';
import { ExpiredInternshipPageLoadable } from 'modules/listing/pages/internships/expired/ExpiredInternshipPageLoadable';
import { UnpublishedInternshipPageLoadable } from 'modules/listing/pages/internships/unpublished/UnpublishedInternshipPageLoadable';
import { ViewInternshipPageLoadable } from 'modules/listing/pages/internships/view/ViewInternshipPageLoadable';
import { SearchInternshipRefinements } from 'modules/search/pages/Internships/components/SearchInternshipRefinements';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import type { AppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { renderLoader } from 'routing/utils/renderLoader/renderLoader';
import { getUserEnvironment } from 'zustand-stores/userEnvironmentStore';

import { internshipSegmentedRoutes } from './internship.routes';

/**
 * Internship routes
 *
 * These routes are separate in their own file to facilitate maintenance, and
 * they're used on `main.route-objects.tsx`
 */
export const internshipRouteObjects = internshipSegmentedRoutes.map(
  (segmentedRoute) => ({
    path: segmentedRoute.fullPath,
    ...renderLoader({
      loader: async ({ params }: AppRouteLoaderArgs<typeof segmentedRoute>) => {
        const { internshipIdDashSlug = '' } = params;

        const { user } = getUserEnvironment();

        try {
          const [internship] = await Promise.all([
            listingApiFetchInternship(internshipIdDashSlug.split('-')[0]),
            // It's possible this loadable won't be used if the internship is unpublished
            // but in most cases optimistically loading it is best
            ViewInternshipPageLoadable.load(),
          ]);

          switch (internship.type) {
            case 'INTERNSHIP':
              return {
                type: isListingExpired(internship.expires)
                  ? 'EXPIRED_INTERNSHIP'
                  : internship.type,
                internship,
                handle: {
                  hideFooterDivider: Boolean(!user),
                  urlByLocale: internship.url,
                } satisfies LayoutHandleProps,
              } as const;
            case 'UNPUBLISHED_INTERNSHIP':
              await UnpublishedInternshipPageLoadable.load();
              return {
                type: internship.type,
                unpublishedInternship: internship,
              } as const;
          }
        } catch {
          return null;
        }
      },
      render: (data) => {
        switch (data?.type) {
          case undefined:
            return <NotFoundPageLoadable noLayout />;
          case 'INTERNSHIP':
            return <ViewInternshipPageLoadable internship={data.internship} />;
          case 'EXPIRED_INTERNSHIP':
            return (
              <ExpiredInternshipPageLoadable internship={data.internship} />
            );
          case 'UNPUBLISHED_INTERNSHIP':
            return (
              <UnpublishedInternshipPageLoadable
                unpublishedInternship={data.unpublishedInternship}
              />
            );
        }
      },
    }),
    handle: {
      searchRefinements: <SearchInternshipRefinements />,
      defaultSearchType: 'INTERNSHIP',
      showDesktopSearch: true,
      hideFooterDivider: true,
    } satisfies LayoutHandleProps,
  }),
) satisfies RouteObject[];
